import React, { useEffect, useState } from 'react';
import { 
  Modal, 
  Form, 
  Input, 
  TreeSelect, 
  Switch,
  message,
  Alert,
  Divider
} from 'antd';
import { 
  BookOutlined, 
  FolderOutlined,
  FileTextOutlined
} from '@ant-design/icons';
import { SpecialtyTree, specialtyAPI } from '../../services/specialtyAPI';
import { UserCompany, Company } from '../../services/companyAPI';

const { TextArea } = Input;

// Union tip yaradırıq
type SelectedCompany = UserCompany | Company;

interface SpecialtyFormProps {
  visible: boolean;
  specialty: SpecialtyTree | null;
  parentSpecialty: SpecialtyTree | null;
  onCancel: () => void;
  onSuccess: () => void;
  selectedCompany: SelectedCompany | null;
}

const SpecialtyForm: React.FC<SpecialtyFormProps> = ({ 
  visible, 
  specialty, 
  parentSpecialty,
  onCancel, 
  onSuccess, 
  selectedCompany 
}) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [specialtyOptions, setSpecialtyOptions] = useState<any[]>([]);
  const [loadingSpecialties, setLoadingSpecialties] = useState(false);

  useEffect(() => {
    if (visible && selectedCompany) {
      loadSpecialtyOptions();
      
      if (specialty) {
        // Redaktə rejimi
        form.setFieldsValue({
          name: specialty.name,
          parent: specialty.parent,
          description: specialty.description,
          is_active: specialty.is_active,
          company: specialty.company
        });
      } else {
        // Yeni ixtisas əlavə etmə rejimi
        form.resetFields();
        form.setFieldsValue({ 
          company: selectedCompany.id,
          is_active: true,
          parent: parentSpecialty?.id || undefined
        });
      }
    }
  }, [visible, specialty, parentSpecialty, selectedCompany]);

  const loadSpecialtyOptions = async () => {
    if (!selectedCompany) return;
    
    setLoadingSpecialties(true);
    try {
      const response = await specialtyAPI.getSpecialtySelectOptions(selectedCompany.id);
      if (response.success && response.data) {
        // TreeSelect üçün hierarchical struktur yaradırıq
        const treeData = buildTreeData(response.data);
        setSpecialtyOptions(treeData);
      }
    } catch (error) {
      console.error('İxtisaslar yüklənmədi:', error);
      message.error('İxtisaslar yüklənmədi');
    } finally {
      setLoadingSpecialties(false);
    }
  };

  const buildTreeData = (specialties: any[]) => {
    const specialtyMap = new Map();
    const rootSpecialties: any[] = [];

    // Redaktə edilən ixtisası çıxar ki, özünü parent kimi seçə bilməsin
    const filteredSpecialties = specialties.filter(c => c.value !== specialty?.id);

    // Əvvəlcə bütün ixtisasları map-ə əlavə et
    filteredSpecialties.forEach(specialtyItem => {
      specialtyMap.set(specialtyItem.value, {
        title: specialtyItem.label,
        value: specialtyItem.value,
        key: specialtyItem.value,
        children: [],
        level: specialtyItem.level,
        parent: specialtyItem.parent
      });
    });

    // Sonra parent-child əlaqələrini qur
    filteredSpecialties.forEach(specialtyItem => {
      const specialtyNode = specialtyMap.get(specialtyItem.value);
      if (specialtyItem.parent) {
        const parentNode = specialtyMap.get(specialtyItem.parent);
        if (parentNode) {
          parentNode.children.push(specialtyNode);
        }
      } else {
        rootSpecialties.push(specialtyNode);
      }
    });

    return rootSpecialties;
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      setLoading(true);

      const specialtyData = {
        name: values.name,
        parent: values.parent || null,
        company: values.company,
        description: values.description || '',
        is_active: values.is_active
      };

      let response;
      if (specialty) {
        response = await specialtyAPI.updateSpecialty(specialty.id, specialtyData);
      } else {
        response = await specialtyAPI.createSpecialty(specialtyData);
      }

      if (response.success) {
        message.success(response.message || (specialty ? 'İxtisas yeniləndi' : 'İxtisas əlavə edildi'));
        onSuccess();
      } else {
        console.error('API error:', response);
        message.error(response.message || 'Əməliyyat uğursuz oldu');
        if (response.errors) {
          console.error('Validation errors:', response.errors);
        }
      }
    } catch (error) {
      console.error('Form xətası:', error);
      message.error('Form məlumatlarını yoxlayın');
    } finally {
      setLoading(false);
    }
  };

  const getModalTitle = () => {
    if (specialty) {
      return 'İxtisası Redaktə Et';
    } else if (parentSpecialty) {
      return `"${parentSpecialty.name}" ixtisasına alt ixtisas əlavə et`;
    } else {
      return 'Yeni İxtisas Əlavə Et';
    }
  };

  return (
    <Modal
      title={
        <div className="flex items-center">
          <BookOutlined className="mr-2 text-green-500" />
          {getModalTitle()}
        </div>
      }
      open={visible}
      onCancel={onCancel}
      onOk={handleSubmit}
      confirmLoading={loading}
      width={600}
      okText={specialty ? 'Yenilə' : 'Əlavə Et'}
      cancelText="Ləğv Et"
      destroyOnClose
    >
      <Form
        form={form}
        layout="vertical"
        requiredMark={false}
      >
        {parentSpecialty && (
          <Alert
            message={`Ana ixtisas: ${parentSpecialty.name}`}
            description="Bu ixtisas seçilmiş ana ixtisasın altında yaradılacaq"
            type="info"
            showIcon
            className="mb-4"
          />
        )}

        {/* Əsas məlumatlar */}
        <Divider>
          <FolderOutlined className="mr-2" />
          İxtisas məlumatları
        </Divider>

        <Form.Item
          name="name"
          label="İxtisas adı"
          rules={[
            { required: true, message: 'İxtisas adı tələb olunur!' },
            { min: 2, message: 'İxtisas adı ən azı 2 simvol olmalıdır!' },
            { max: 200, message: 'İxtisas adı maksimum 200 simvol ola bilər!' }
          ]}
        >
          <Input 
            placeholder="İxtisasın adını daxil edin"
            prefix={<BookOutlined className="text-gray-400" />}
          />
        </Form.Item>

        <Form.Item
          name="parent"
          label="Ana ixtisas"
          help="Bu ixtisas hansı ixtisasın altında yerləşəcək (boş buraxsanız ana ixtisas olacaq)"
        >
          <TreeSelect
            placeholder="Ana ixtisas seçin (ixtiyari)"
            treeData={specialtyOptions}
            loading={loadingSpecialties}
            style={{ width: '100%' }}
            dropdownStyle={{ maxHeight: 400, overflow: 'auto' }}
            treeDefaultExpandAll
            allowClear
            showSearch
            treeNodeFilterProp="title"
          />
        </Form.Item>

        <Form.Item
          name="description"
          label="Təsvir"
          help="İxtisas haqqında qısa məlumat"
        >
          <TextArea
            rows={3}
            placeholder="İxtisasın təsvirini daxil edin..."
            maxLength={500}
            showCount
          />
        </Form.Item>

        {/* Status */}
        <Divider>
          <FileTextOutlined className="mr-2" />
          Status
        </Divider>

        <Form.Item
          name="is_active"
          label="Aktiv status"
          valuePropName="checked"
          help="Aktiv ixtisaslar müəllimlərə təyin edilə bilər"
        >
          <Switch 
            checkedChildren="Aktiv" 
            unCheckedChildren="Deaktiv"
          />
        </Form.Item>

        {/* Gizli sahələr */}
        <Form.Item name="company" hidden>
          <Input />
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default SpecialtyForm;